'use client';

import { useTranslations } from 'next-intl';
import { Suspense, useState } from 'react';
import AgeRestrictionBadge from '@/components/shared/AgeRestrictionBadge';
import createModalWrapper from '@/modals/components/createModalWrapper';
import Modal from '@/modals/components/Modal';
import { modalMotionPresets } from '@/modals/components/Modal/modalMotion';
import { cn } from '@/utils/cn';
import { useCloseAuthModal } from '../hooks/useCloseAuthModal';
import { setFirstVisitRegistration } from '../RegisterModal/visitRegistration.utils';
import { LoginPopupForm } from './components/LoginPopupForm';
import { ResetPasswordPopupForm } from './components/ResetPasswordPopupForm';
import ResetPasswordSuccessPopup from './components/ResetPasswordSuccessPopup';
import { StepOfLogin } from './type';

type ModalData = {
  isMakeVisitAfterRegistration?: boolean;
};
interface LoginModalProps {
  onCloseAction: () => void;
  initialStep?: StepOfLogin;
  initialEmail?: string;
  modalData?: ModalData;
}

const LoginModal = createModalWrapper<LoginModalProps>(({ onCloseAction, initialStep = StepOfLogin.LOGIN, initialEmail = '', modalData }) => {
  const t = useTranslations('sign-in');

  const { handleCloseModal } = useCloseAuthModal({ page: '/game/', onCloseAction: () => {
    if (modalData?.isMakeVisitAfterRegistration) {
      setFirstVisitRegistration(false);
    }
    onCloseAction();
  } });

  const [stepOfLogin, setStepOfLogin] = useState<StepOfLogin>(initialStep);
  const [email, setEmail] = useState<string>(initialEmail);

  const isResetPasswordModal = stepOfLogin === StepOfLogin.RESET_PASSWORD;
  const isSuccessResetModal = stepOfLogin === StepOfLogin.SUCCESS_RESET;
  const isResetModals = isResetPasswordModal || isSuccessResetModal;

  const backgroundVariant = isResetModals ? 'default' : 'gradient';
  const headerTitle = isResetModals ? t('reset-password') : t('title');
  const headerBack = isResetModals ? () => setStepOfLogin(StepOfLogin.LOGIN) : undefined;

  return (
    <Modal
      headerTitle={headerTitle}
      onCloseAction={handleCloseModal}
      onHeaderBack={headerBack}
      headerClassName="lg:pb-2"
      bodyClassName={cn(
        isSuccessResetModal && 'max-lg:pr-[0.938rem] max-lg:pl-[1.063rem]',
      )}
      contentClassName="lg:w-[23.75rem] lg:h-[44.6875rem] lg:rounded-3xl hide-scrollbar overflow-visible"
      variant={backgroundVariant}
      motionPreset={modalMotionPresets.none}
      headerChild={stepOfLogin === StepOfLogin.LOGIN ? <AgeRestrictionBadge /> : undefined}
    >
      {() => (
        <>
          {isResetPasswordModal && (
            <ResetPasswordPopupForm
              setStepOfLogin={setStepOfLogin}
              setEmail={setEmail}
            />
          )}

          {isSuccessResetModal && (
            <ResetPasswordSuccessPopup
              email={email}
            />
          )}

          {stepOfLogin === StepOfLogin.LOGIN && (
            <Suspense>
              <LoginPopupForm
                setStepOfLogin={setStepOfLogin}
                closeModal={onCloseAction}
              />
            </Suspense>
          )}
        </>
      )}
    </Modal>
  );
});

export default LoginModal;
